/**
 * Trajectory lines (S1-10, S2-09, rebuilt for the fleet in S3-12): sampled true states, mapped through M, revealed up
 * to the current date, plus a "head" segment to the sprite.
 *
 * Rendering (ribbonMaterial.ts): one instanced strip per spacecraft with float64 precision via high/low parts, and
 * all brightness (comet tail, orbiter memory, silent dimming, dashed prediction, highlight) computed in the shader
 * from each sample's mission time. Two draw calls per spacecraft (ribbon + halo), whatever the trail's length.
 *
 * Line frame (rebuilt after the Sprint 3 review): every sample is mapped *once* with M as it was at the sample's own
 * time — planets and moons where they were then — which is exactly where the spacecraft's icon was drawn at that
 * moment. That curve is static, continuous and meets the spacecraft. (Sprint 2 mapped the samples with the bodies
 * where they are *now*: samples drifted into and out of a moving planet's or moon's bubble every frame, and a
 * 90-day orbiter trail, smeared along Saturn's own motion, was re-magnified around Saturn's current position — the
 * trails shook and changed shape during playback.) Near a planet, the curve is then shifted rigidly with the planet
 * (body-relative frame): by w·(planet now − planet then), w = 1 deep in the bubble and 0 at its edge, so orbits
 * stay closed around the planet and a flyby's hyperbola stays wrapped around it. A flyby sample stops moving with
 * the planet HOLD_DAYS after its time and then eases, over SETTLE_DAYS, into its unmagnified (global) position: an
 * old flyby is a gentle bend where the planet was then, not a magnified loop in empty space (Sprint 1's lesson). An
 * orbital phase's samples move with their planet as long as they are shown.
 *
 * Lazy sampling (S3-12, D10): evaluating a sample's true state needs ephemeris lookups, and Cassini's 13 years at
 * Saturn alone are ~60,000 line samples. Only *required* samples are computed — revealed (t ≤ now) and, inside an
 * orbital phase, younger than the trail memory — newest first, within a frame budget shared by all lines. Older
 * phase samples are never drawn (the shader drops segments beyond the memory), so they are never computed unless
 * time goes back.
 */
import * as THREE from 'three';
import { COARSE_TRACK_ERROR_KM, ORBIT_RANGE_AU, PLUTO_KEPLER_ERROR_KM } from '../core/ephemeris';
import { findInterval } from '../core/hermite';
import { createState } from '../core/trajectory';
import { AU_KM } from '../core/units';
import { MoonTrack, boundToMoon } from '../core/moons';
import { type Vec3, length, vec3 } from '../core/vec3';
import {
  NEVER,
  type RibbonUniforms,
  createRibbonGeometry,
  createRibbonMaterial,
  createRibbonUniforms,
  splitDouble,
} from './ribbonMaterial';
import type { World, WorldSpacecraft } from './world';

export type LineFrame = 'heliocentric' | 'body-relative';

/** An event drawn as a marker on the trajectory (S1-22), at days since J2000 TDB. */
export interface TrajectoryMarker {
  t: number;
  type: string;
}

/** How a line is drawn (life cycle D11, orbiter memory D10); times in days since J2000 TDB. */
export interface LineStyle {
  /** The trail is dimmed after this (contact lost); Infinity = never. */
  silentFrom: number;
  /** Orbital phase [start, end]: samples inside it fade out over the trail memory (D10). */
  phase: [number, number] | null;
}

/** Per-frame time budget for the one-off trajectory precomputation, shared by all lines (see beginFrame). */
const SAMPLE_BUDGET_MS = 8;
let frameDeadline = Number.NaN;

/**
 * Starts a frame's shared precomputation budget: all lines together spend at most `budgetMs` on sampling per frame
 * (with six spacecraft, a budget per line stretched start-up frames to 50–80 ms).
 */
export function beginFrame(budgetMs = SAMPLE_BUDGET_MS): void {
  beginBetweenFrames(budgetMs);
  requiredSampled = false;
}

/**
 * Starts the shared budget of a sampling task between frames (S4-03 part 6, see TrajectoryLine.sampleBetweenFrames).
 * Unlike beginFrame, it keeps the frame's precomputedThisFrame flag: the next frame's interval includes this task.
 */
export function beginBetweenFrames(budgetMs: number): void {
  const now = performance.now();
  frameDeadline = now + budgetMs;
  // the look-ahead (samples not shown yet) gets only the first quarter of the budget (S4-15: on a phone it used the
  // whole 8 ms of every frame during playback)
  lookaheadDeadline = now + budgetMs / 4;
}
let lookaheadDeadline = Number.NaN;
let requiredSampled = false;

/**
 * Whether a line computed required samples since beginFrame: one-off work that makes a frame slower without saying
 * anything about the device, so the automatic quality does not judge such frames (S4-15).
 */
export function precomputedThisFrame(): boolean {
  return requiredSampled;
}

/**
 * Ribbon width in CSS px. Maintainer feedback (S4-15): on phones "die dicken blauen Linien dominieren", then
 * everywhere "Die blauen Spuren sind zu viel, zu dick, zu auffällig" — thin lines, a dimmer tail, and the glow only
 * on the selected (followed) mission, which keeps a wider line so its path still stands out.
 */
const WIDTH_PX = 1.5;
const WIDTH_PX_NARROW = 1;
const WIDTH_PX_SELECTED = 2;
/** Below this short side (CSS px) the screen counts as a phone. */
const NARROW_PX = 500;
/** Glow halo around the ribbon on the post-processed tiers. */
const HALO_WIDTH_PX = 6;
const HALO_OPACITY = 0.13;
/** Comet tail (S2-09): brightness decays from the head gain to BASE with this time constant, days of mission time. */
const TAIL_DAYS = 60;
const BASE = 0.3;
const HEAD_HDR = 3.2;
const HEAD_LDR = 1;
/** Orbiter trail memory (D10), days of mission time; samples a little older are still computed for the fade. */
export const MEMORY_DAYS = 90;
const MEMORY_MARGIN_DAYS = 5;
/** Silent spacecraft: trail brightness factor (D11). */
const SILENT_GAIN = 0.5;
/** Non-selected missions while one is selected (D9). */
export const DIMMED = 0.45;
/** Dash period of the predicted part, days of mission time. */
const DASH_DAYS = 12;
/**
 * Dot period of a reconstructed trajectory (S4-09), days of mission time: ~4 dots per 2-hour lunar orbit, ~150 on the
 * 3-day coast to the Moon — short dots, far finer than the prediction's dashes, so the two never look alike.
 */
const DOT_DAYS = 0.02;
/** A flyby sample moves with its planet for this long after its time, days of mission time (then it settles). */
export const HOLD_DAYS = 30;
/** After the hold, a flyby sample eases into its global (unmagnified) position over this many days. */
export const SETTLE_DAYS = 30;
/** Inside this fraction of the bubble radius a sample moves fully with its planet; the shift fades out to the edge. */
const RIGID_INNER = 0.3;
/**
 * A sample's planet (and moon) context is kept when it is within this many bubble radii, so a mode switch that grows
 * a bubble can re-map it without new ephemeris lookups.
 */
const CONTEXT_REACH = 2;
/**
 * Samples re-mapped per frame and line after a scale-mode change, newest first (S4-15 item 3): ~2.5 ms on a phone; the
 * trail near the spacecraft follows the morph, the older part fades out over STALE_FADE_MS and grows back afterwards.
 */
const REMAP_PER_FRAME = 400;
const STALE_FADE_MS = 200;

/** Samples computed ahead of the reveal when budget is left, so playback never waits for them. */
const LOOKAHEAD = 256;
/** Flyby beacons (S2-10): style-B signal red (theme.css --red), brighter than 1 with post-processing. */
const BEACON_COLOR = '#f0574a';
const BEACON_HDR = 2.6;
/** Beacons not reached yet are dimmer; the next one pulses (a waypoint to fly to). */
const BEACON_FUTURE = 0.55;

export type Highlight = 'normal' | 'selected' | 'dimmed';

export class TrajectoryLine {
  readonly root = new THREE.Group();
  /** Number of line samples; marker states follow them in the per-sample arrays. */
  private readonly n: number;
  private readonly total: number;
  private readonly t: Float64Array;
  /** Sorted line sample times only (`t` continues with the unsorted marker times). */
  private readonly lineT: Float64Array;
  /** Mission time of sample 0: the shader works with float32 times relative to it. */
  private readonly epoch: number;
  private readonly markerSprites: { t: number; sprite: THREE.Sprite }[] = [];
  /**
   * Per sample: true heliocentric position (km); fixed display position — M with the bodies where they were at the
   * sample's time (du); current display position (the fixed one, shifted with its planet).
   */
  private readonly pos: Float64Array;
  private readonly fixed: Float64Array;
  private readonly disp: Float64Array;
  /** Per sample: computed (1) or not (0). */
  private readonly computed: Uint8Array;
  /**
   * Per sample, its surroundings at its own time: the nearest planet with a bubble (index into world.planets, -1 =
   * none) and its true position then; a moon of it with a nested bubble nearby (index into world.moons) and its
   * offset from the planet then. Re-mapping (mode switch) needs nothing else.
   */
  private readonly ctxPlanet: Int8Array;
  private readonly ctxPos: Float64Array;
  private readonly ctxMoon: Int8Array;
  private readonly ctxMoonQ: Float64Array;
  /** Per sample: how much it moves with its planet (1 deep inside the bubble, 0 from its edge on). */
  private readonly weight: Float32Array;
  /**
   * Per sample near a moon (S4-13): how much it moves with that moon instead (1 deep inside the moon's nested bubble,
   * 0 from its edge on), and the moon's display position at the sample's time. Columbia's 55 hours in lunar orbit
   * moved only with Earth and were drawn as loops smeared along the Moon's path; now they stay a closed orbit around
   * the Moon, as a planet's orbiter trail does around its planet.
   */
  private readonly moonWeight: Float32Array;
  private readonly moonThen: Float64Array;
  /**
   * Per sample: the moon (index into world.moons, -1 = none) of its encounter segment, if the spacecraft is in orbit
   * around it — only those samples move with a moon; a flyby's (Voyager at Ganymede, Cassini at Titan) do not.
   */
  private readonly boundMoon: Int8Array;
  /** Profile version each sample's fixed position was mapped with. */
  private readonly mappedWith: Int32Array;
  /** Samples [moveLo, moveHi) were placed with the planets' current positions last frame. */
  private moveLo = 0;
  private moveHi = 0;
  private bodyRelative = true;
  /** GPU strip: n samples plus one slot for the head; high and low float32 parts; times relative to the epoch. */
  private readonly high: Float32Array;
  private readonly low: Float32Array;
  private readonly times: Float32Array;
  private readonly highBuffer: THREE.InstancedInterleavedBuffer;
  private readonly lowBuffer: THREE.InstancedInterleavedBuffer;
  private readonly timeBuffer: THREE.InstancedInterleavedBuffer;
  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly uniforms: RibbonUniforms;

  /** The trail is drawn dotted: a reconstructed trajectory (S4-09). */
  get dotted(): boolean {
    return this.uniforms.uDotted.value > 0.5;
  }
  private readonly ribbon: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  /** Strip slot currently holding the head (the spacecraft position), or -1. */
  private headSlot = -1;
  private dirtyMin = Infinity;
  private dirtyMax = -1;
  private timeMin = Infinity;
  private timeMax = -1;
  private readonly beaconColor = new THREE.Color(BEACON_COLOR);
  private hdr = false;
  private narrow = false;
  private highlight: Highlight = 'normal';
  private style: LineStyle = { silentFrom: Infinity, phase: null };
  private profileVersion = -1;
  private lastFrameMode: LineFrame | null = null;
  private lastRemapTdb = Number.NaN;
  private hint = 0;
  /** Index of the first sample of the phase and of the first sample after it (n if none). */
  private phaseStart: number;
  private phaseEnd: number;
  private requiredReady = false;
  /** The revealed sample count of the last update, for sampling between frames. */
  private lastRevealed = 0;
  /** The last sampling pass stopped at its deadline: samples are still to be computed. */
  private pendingSamples = true;
  private readonly ranges: number[] = [];
  private readonly sampleState = createState();
  private readonly tmp = vec3();
  private readonly tmpA = vec3();
  private readonly tmpB = vec3();
  private readonly tmpC = vec3();

  constructor(
    private readonly world: World,
    private readonly sc: WorldSpacecraft,
    markers: readonly TrajectoryMarker[] = [],
    markerTexture?: THREE.Texture,
    /** Precomputed line sample times (the loader's worker computes them, S3-03). */
    precomputedTimes?: Float64Array,
  ) {
    const trajectory = sc.trajectory!;
    const lineTimes = precomputedTimes ?? trajectory.sampleTimes((center) => world.moonBands(center));
    const [start, end] = trajectory.coverage();
    const inside = markers.filter((mk) => mk.t >= start && mk.t <= end);
    this.n = lineTimes.length;
    this.total = this.n + inside.length;
    this.t = new Float64Array(this.total);
    this.t.set(lineTimes);
    inside.forEach((mk, j) => (this.t[this.n + j] = mk.t));
    this.lineT = this.t.subarray(0, this.n);
    this.epoch = lineTimes[0] ?? 0;
    this.pos = new Float64Array(3 * this.total);
    this.fixed = new Float64Array(3 * this.total);
    this.disp = new Float64Array(3 * this.total);
    this.computed = new Uint8Array(this.total);
    this.ctxPlanet = new Int8Array(this.total).fill(-1);
    this.ctxPos = new Float64Array(3 * this.total);
    this.ctxMoon = new Int8Array(this.total).fill(-1);
    this.ctxMoonQ = new Float64Array(3 * this.total);
    this.weight = new Float32Array(this.total);
    this.moonWeight = new Float32Array(this.total);
    this.moonThen = new Float64Array(3 * this.total);
    this.boundMoon = new Int8Array(this.total).fill(-1);
    this.mappedWith = new Int32Array(this.total).fill(-1);
    this.phaseStart = this.n;
    this.phaseEnd = this.n;

    for (const mk of inside) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: markerTexture ?? null,
          color: new THREE.Color(BEACON_COLOR),
          sizeAttenuation: false,
          transparent: true,
          depthTest: false,
          depthWrite: false,
        }),
      );
      sprite.renderOrder = 9;
      sprite.name = `${sc.id}:${mk.type}`;
      sprite.visible = false;
      this.root.add(sprite);
      this.markerSprites.push({ t: mk.t, sprite });
    }

    // the strip: instance i is the segment from slot i to slot i + 1 (same buffer, offset by one sample)
    const slots = this.n + 1;
    this.high = new Float32Array(3 * slots);
    this.low = new Float32Array(3 * slots);
    this.times = new Float32Array(slots);
    for (let i = 0; i < this.n; i++) this.times[i] = this.t[i]! - this.epoch;
    this.times[this.n] = this.times[this.n - 1] ?? 0;
    this.highBuffer = new THREE.InstancedInterleavedBuffer(this.high, 3, 1).setUsage(THREE.DynamicDrawUsage);
    this.lowBuffer = new THREE.InstancedInterleavedBuffer(this.low, 3, 1).setUsage(THREE.DynamicDrawUsage);
    this.timeBuffer = new THREE.InstancedInterleavedBuffer(this.times, 1, 1).setUsage(THREE.DynamicDrawUsage);
    const g = (this.geometry = createRibbonGeometry());
    g.setAttribute('instanceStart', new THREE.InterleavedBufferAttribute(this.highBuffer, 3, 0));
    g.setAttribute('instanceEnd', new THREE.InterleavedBufferAttribute(this.highBuffer, 3, 3));
    g.setAttribute('instanceStartLow', new THREE.InterleavedBufferAttribute(this.lowBuffer, 3, 0));
    g.setAttribute('instanceEndLow', new THREE.InterleavedBufferAttribute(this.lowBuffer, 3, 3));
    g.setAttribute('instanceTStart', new THREE.InterleavedBufferAttribute(this.timeBuffer, 1, 0));
    g.setAttribute('instanceTEnd', new THREE.InterleavedBufferAttribute(this.timeBuffer, 1, 1));
    g.instanceCount = 0;

    const u = (this.uniforms = createRibbonUniforms(new THREE.Color(sc.color)));
    u.uTail.value = TAIL_DAYS;
    u.uBase.value = BASE;
    u.uMemory.value = MEMORY_DAYS;
    u.uSilentGain.value = SILENT_GAIN;
    u.uDash.value = DASH_DAYS;
    u.uDot.value = DOT_DAYS;
    u.uDotted.value = sc.reconstructed ? 1 : 0;
    this.ribbon = new THREE.Mesh(g, createRibbonMaterial(u));
    this.ribbon.frustumCulled = false;
    this.ribbon.renderOrder = 3;
    this.ribbon.name = `${sc.id}:trail`;
    this.halo = new THREE.Mesh(
      g,
      createRibbonMaterial(u, { linewidth: { value: HALO_WIDTH_PX }, uOpacity: { value: HALO_OPACITY } }),
    );
    this.halo.frustumCulled = false;
    this.halo.renderOrder = 2;
    this.halo.visible = false;
    this.root.add(this.halo, this.ribbon);
    this.root.visible = false;
  }

  /**
   * Whether every sample needed at the current date has been computed (the line is drawn from then on) and is mapped
   * with the current profile (no stale part after a mode change).
   */
  get ready(): boolean {
    return this.requiredReady && this.freshFrom === -Infinity;
  }

  /** Number of line samples (tests, debugState). */
  get sampleCount(): number {
    return this.n;
  }

  /** How many line samples are computed (tests and debugState: lazy sampling). */
  get computedCount(): number {
    let c = 0;
    for (let i = 0; i < this.n; i++) c += this.computed[i]!;
    return c;
  }

  /** Number of segments drawn this frame (including the head). */
  get drawnSegments(): number {
    return this.geometry.instanceCount;
  }

  /** Ribbon resolution (CSS px): the shader sizes the ribbon in pixels. */
  setResolution(width: number, height: number): void {
    this.uniforms.resolution.value.set(width, height);
    this.narrow = Math.min(width, height) < NARROW_PX;
    this.applyGain();
  }

  /** HDR on/off (S2-07 tiers): the newest part of the trail only exceeds 1 (and blooms) with post-processing. */
  setHdr(on: boolean): void {
    this.hdr = on;
    this.applyGain();
  }

  /** Selection highlight (D9): the selected mission at full strength, the others dimmed and without the HDR glow. */
  setHighlight(level: Highlight): void {
    if (level === this.highlight) return;
    this.highlight = level;
    this.applyGain();
  }

  private applyGain(): void {
    const dimmed = this.highlight === 'dimmed';
    this.uniforms.uHead.value = this.hdr && !dimmed ? HEAD_HDR : HEAD_LDR;
    this.uniforms.uHighlight.value = dimmed ? DIMMED : 1;
    const selected = this.highlight === 'selected';
    this.uniforms.linewidth.value = selected ? WIDTH_PX_SELECTED : this.narrow ? WIDTH_PX_NARROW : WIDTH_PX;
    this.halo.visible = this.hdr && selected && !this.narrow;
  }

  /** Life cycle and orbital phase (D10/D11). */
  setStyle(style: LineStyle): void {
    this.style = style;
    const u = this.uniforms;
    u.uSilentFrom.value = Number.isFinite(style.silentFrom) ? style.silentFrom - this.epoch : NEVER;
    if (style.phase) {
      u.uPhase.value.set(style.phase[0] - this.epoch, style.phase[1] - this.epoch);
      this.phaseStart = lowerBound(this.lineT, style.phase[0]);
      this.phaseEnd = lowerBound(this.lineT, style.phase[1]);
    } else {
      u.uPhase.value.set(NEVER, -NEVER);
      this.phaseStart = this.phaseEnd = this.n;
    }
  }

  getStyle(): LineStyle {
    return this.style;
  }

  /**
   * Required sample ranges at `now` with `revealed` samples: everything revealed, except phase samples older than the
   * memory (plus a margin for the fade). Written into `ranges` as [a0, b0, a1, b1, …], half-open, ascending.
   */
  private requiredRanges(now: number, revealed: number): void {
    const out = this.ranges;
    out.length = 0;
    const push = (a: number, b: number): void => {
      if (b <= a) return;
      if (out.length && out[out.length - 1]! >= a) out[out.length - 1] = Math.max(out[out.length - 1]!, b);
      else out.push(a, b);
    };
    push(0, Math.min(revealed, this.phaseStart));
    if (revealed > this.phaseStart) {
      const recent = lowerBound(this.lineT, now - MEMORY_DAYS - MEMORY_MARGIN_DAYS) - 1;
      push(Math.max(this.phaseStart, recent), Math.min(revealed, this.phaseEnd));
      push(this.phaseEnd, revealed);
    }
  }

  /**
   * Computes required samples (newest first), then the markers, then a look-ahead, until the shared frame deadline.
   * Returns true when every required sample is computed.
   */
  private sample(revealed: number): boolean {
    const deadline = Number.isNaN(frameDeadline) ? performance.now() + SAMPLE_BUDGET_MS : frameDeadline;
    // a sample can cost a few ephemeris nodes (BodyTrack): check the clock before each one (S4-03 part 6: every
    // fourth let a 2 ms budget run to 10)
    const over = (): boolean => performance.now() > deadline;
    this.pendingSamples = true;
    const r = this.ranges;
    for (let k = r.length - 2; k >= 0; k -= 2) {
      for (let i = r[k + 1]! - 1; i >= r[k]!; i--) {
        if (this.computed[i]) continue;
        if (over()) return false;
        this.computeSample(i);
        requiredSampled = true;
      }
    }
    for (let i = this.n; i < this.total; i++) {
      if (this.computed[i]) continue;
      if (over()) return true;
      this.computeSample(i);
      requiredSampled = true;
    }
    const ahead = Number.isNaN(lookaheadDeadline) ? deadline : lookaheadDeadline;
    for (let i = revealed; i < Math.min(this.n, revealed + LOOKAHEAD); i++) {
      if (this.computed[i]) continue;
      if (performance.now() > ahead) return true;
      this.computeSample(i);
    }
    this.pendingSamples = false;
    return true;
  }

  /**
   * Samples in a task between frames, against the shared deadline of beginBetweenFrames (S4-03 part 6): the trails of
   * the start sampled 8 ms inside each frame, and Lighthouse's 4× CPU made every such frame on a fast machine a long
   * task. The frame keeps a small budget for what it shows at once; this does the bulk. Returns true while samples
   * are left for another task.
   */
  sampleBetweenFrames(): boolean {
    if (this.n === 0 || !this.pendingSamples) return false;
    this.requiredReady = this.sample(this.lastRevealed);
    return this.pendingSamples;
  }

  /** Samples are left to compute (sampleBetweenFrames has work). */
  get pending(): boolean {
    return this.n > 0 && this.pendingSamples;
  }

  /** Evaluates the true state of sample `i`, finds its surroundings at its time and maps it. */
  private computeSample(i: number): void {
    const st = this.sampleState;
    this.sc.trajectory!.stateAt(this.t[i]!, this.world.bodyAtFastFn, st, false);
    // the relative velocity of an encounter segment is there without the heliocentric one
    this.boundMoon[i] =
      st.frame !== 'sun' && boundToMoon(st.frame, st.q, st.w)
        ? this.world.moons.findIndex((m) => m.data.id === st.frame)
        : -1;
    const k = 3 * i;
    this.pos[k] = st.p.x;
    this.pos[k + 1] = st.p.y;
    this.pos[k + 2] = st.p.z;
    this.computed[i] = 1;
    this.findContext(i);
    this.mapFixed(i);
  }

  /**
   * The planet (and moon) near sample `i` at its time: the one with the smallest distance in bubble radii, within
   * CONTEXT_REACH radii. Planets whose orbit is far from the sample's distance to the Sun are not even evaluated.
   */
  private findContext(i: number): void {
    const world = this.world;
    const m = world.mapping;
    const t = this.t[i]!;
    const k = 3 * i;
    const p = this.tmp;
    p.x = this.pos[k]!;
    p.y = this.pos[k + 1]!;
    p.z = this.pos[k + 2]!;
    const r = length(p);
    const b = this.tmpB;
    let best = -1;
    let bestRel = CONTEXT_REACH;
    if (this.reachVersion !== this.profileVersion) {
      this.reachVersion = this.profileVersion;
      world.planets.forEach((body, j) => {
        const range = ORBIT_RANGE_AU[body.data.id as keyof typeof ORBIT_RANGE_AU];
        this.reachKm[j] =
          range && body.mapped.bubbleRadius > 0 ? CONTEXT_REACH * m.bubbleRadius(body.mapped, range[1] * AU_KM) : 0;
      });
    }
    world.planets.forEach((body, j) => {
      if (body.mapped.bubbleRadius <= 0) return;
      const range = ORBIT_RANGE_AU[body.data.id as keyof typeof ORBIT_RANGE_AU];
      if (range) {
        const reach = this.reachKm[j]!;
        if (r < range[0] * AU_KM - reach || r > range[1] * AU_KM + reach) return;
      }
      // Pluto: its Kepler orbit first — the coarse track's first node integrates an 80-year ephemeris segment, which
      // the outer trails of the start paid for without ever coming near (S4-03 part 6)
      if (world.planetAtRough(body, t, b)) {
        const d = Math.hypot(p.x - b.x, p.y - b.y, p.z - b.z) - PLUTO_KEPLER_ERROR_KM;
        // the bubble radius barely depends on the distance from the Sun (0.05 % here): 1 % covers it
        if (d >= 1.01 * bestRel * m.bubbleRadius(body.mapped, length(b))) return;
      }
      // the outer planets: a coarse position first, the exact one only when the sample may be within reach
      if (world.planetAtCoarse(body, t, b)) {
        const d = Math.hypot(p.x - b.x, p.y - b.y, p.z - b.z) - COARSE_TRACK_ERROR_KM;
        if (d >= bestRel * m.bubbleRadius(body.mapped, length(b))) return;
      }
      if (!world.planetAt(body, t, b)) return;
      const rel = Math.hypot(p.x - b.x, p.y - b.y, p.z - b.z) / m.bubbleRadius(body.mapped, length(b));
      if (rel >= bestRel) return;
      best = j;
      bestRel = rel;
      this.ctxPos[k] = b.x;
      this.ctxPos[k + 1] = b.y;
      this.ctxPos[k + 2] = b.z;
    });
    this.ctxPlanet[i] = best;
    this.ctxMoon[i] = -1;
    if (best < 0) return;
    const planet = world.planets[best]!;
    const rb = Math.hypot(this.ctxPos[k]!, this.ctxPos[k + 1]!, this.ctxPos[k + 2]!);
    const qx = p.x - this.ctxPos[k]!;
    const qy = p.y - this.ctxPos[k + 1]!;
    const qz = p.z - this.ctxPos[k + 2]!;
    const dq = Math.hypot(qx, qy, qz);
    let moonRel = CONTEXT_REACH;
    world.moons.forEach((moon, j) => {
      if (moon.parent !== planet || moon.mapped.bubbleRadius <= 0) return;
      const orbit = moon.data.orbitRadius;
      // a moon on a known orbit can only be near the sample if the sample is near that orbit
      if (orbit && Math.abs(dq - orbit) > 0.5 * orbit) return;
      // Earth's Moon: a coarse position first (its exact track has hourly nodes, three lunar theories each)
      if (world.moonOffsetAtCoarse(moon, t, b)) {
        const d = Math.hypot(qx - b.x, qy - b.y, qz - b.z) - MoonTrack.COARSE_ERROR_KM;
        if (d >= moonRel * m.moonBubbleRadius(moon.mapped, planet.mapped, rb, length(b))) return;
      }
      if (!world.moonOffsetAt(moon, t, b)) return;
      const rBub = m.moonBubbleRadius(moon.mapped, planet.mapped, rb, length(b));
      const rel = Math.hypot(qx - b.x, qy - b.y, qz - b.z) / rBub;
      if (rel >= moonRel) return;
      moonRel = rel;
      this.ctxMoon[i] = j;
      this.ctxMoonQ[k] = b.x;
      this.ctxMoonQ[k + 1] = b.y;
      this.ctxMoonQ[k + 2] = b.z;
    });
  }

  /** CONTEXT_REACH bubble radii per planet at the far edge of its orbit (km), for the profile `reachVersion`. */
  private readonly reachKm: number[] = [];
  private reachVersion = Number.NaN;

  /** Sample whose context `ctxLookup` answers with. */
  private ctxIndex = 0;
  private readonly ctxPlanetP = vec3();
  private readonly ctxMoonP = vec3();
  /** Body positions for M at sample `ctxIndex`'s time: its planet and moon as they were then; no other body. */
  private readonly ctxLookup = (id: string): Vec3 | undefined => {
    const i = this.ctxIndex;
    const j = this.ctxPlanet[i]!;
    if (j < 0) return undefined;
    const k = 3 * i;
    const b = this.ctxPlanetP;
    b.x = this.ctxPos[k]!;
    b.y = this.ctxPos[k + 1]!;
    b.z = this.ctxPos[k + 2]!;
    if (id === this.world.planets[j]!.data.id) return b;
    const mj = this.ctxMoon[i]!;
    if (mj < 0 || id !== this.world.moons[mj]!.data.id) return undefined;
    const mp = this.ctxMoonP;
    mp.x = b.x + this.ctxMoonQ[k]!;
    mp.y = b.y + this.ctxMoonQ[k + 1]!;
    mp.z = b.z + this.ctxMoonQ[k + 2]!;
    return mp;
  };

  /** Maps sample `i` with M as it was at its time (current profile) and places it. */
  private mapFixed(i: number): void {
    const k = 3 * i;
    const m = this.world.mapping;
    const out = this.tmp;
    out.x = this.pos[k]!;
    out.y = this.pos[k + 1]!;
    out.z = this.pos[k + 2]!;
    this.ctxIndex = i;
    m.mapPoint(out, this.ctxLookup, out);
    this.fixed[k] = out.x;
    this.fixed[k + 1] = out.y;
    this.fixed[k + 2] = out.z;
    const j = this.ctxPlanet[i]!;
    let w = 0;
    if (j >= 0) {
      const bx = this.ctxPos[k]!;
      const by = this.ctxPos[k + 1]!;
      const bz = this.ctxPos[k + 2]!;
      const d = Math.hypot(this.pos[k]! - bx, this.pos[k + 1]! - by, this.pos[k + 2]! - bz);
      const rel = d / m.bubbleRadius(this.world.planets[j]!.mapped, Math.hypot(bx, by, bz));
      const x = (rel - RIGID_INNER) / (1 - RIGID_INNER);
      w = x <= 0 ? 1 : x >= 1 ? 0 : 1 - x * x * (3 - 2 * x);
    }
    this.weight[i] = w;
    // near a moon: its display position then, and how much the sample moves with it
    const mj = this.ctxMoon[i]!;
    let wm = 0;
    if (j >= 0 && mj >= 0 && this.boundMoon[i] === mj) {
      const planet = this.world.planets[j]!;
      const moon = this.world.moons[mj]!;
      const b = this.tmpA;
      b.x = this.ctxPos[k]!;
      b.y = this.ctxPos[k + 1]!;
      b.z = this.ctxPos[k + 2]!;
      const q = this.tmpB;
      q.x = this.ctxMoonQ[k]!;
      q.y = this.ctxMoonQ[k + 1]!;
      q.z = this.ctxMoonQ[k + 2]!;
      const md = m.mapRelative(planet.mapped, b, q, this.tmpC);
      this.moonThen[k] = md.x;
      this.moonThen[k + 1] = md.y;
      this.moonThen[k + 2] = md.z;
      const d = Math.hypot(this.pos[k]! - b.x - q.x, this.pos[k + 1]! - b.y - q.y, this.pos[k + 2]! - b.z - q.z);
      const rel = d / m.moonBubbleRadius(moon.mapped, planet.mapped, length(b), length(q));
      const x = (rel - RIGID_INNER) / (1 - RIGID_INNER);
      wm = x <= 0 ? 1 : x >= 1 ? 0 : 1 - x * x * (3 - 2 * x);
    }
    this.moonWeight[i] = wm;
    this.mappedWith[i] = this.profileVersion;
    this.place(i, true);
  }

  private inPhase(t: number): boolean {
    const ph = this.style.phase;
    return !!ph && t >= ph[0] && t <= ph[1];
  }

  /**
   * Places sample `i` at its fixed position, shifted with its planet: w·(planet at the anchor − planet at the
   * sample's time), in display space. The anchor is now, but a flyby sample stops HOLD_DAYS after its time and then
   * settles into its global position over SETTLE_DAYS.
   */
  private place(i: number, force = false): void {
    const k = 3 * i;
    let x = this.fixed[k]!;
    let y = this.fixed[k + 1]!;
    let z = this.fixed[k + 2]!;
    const j = this.ctxPlanet[i]!;
    if (j >= 0) {
      const world = this.world;
      const m = world.mapping;
      const ti = this.t[i]!;
      const now = world.tdb;
      const phase = this.inPhase(ti);
      const w = this.bodyRelative ? this.weight[i]! : 0;
      const anchor = Math.min(Math.max(now, ti), phase ? Infinity : ti + HOLD_DAYS);
      if (w > 0 && anchor > ti) {
        const body = world.planets[j]!;
        // the planet's display shift since the sample's time …
        let sx = 0;
        let sy = 0;
        let sz = 0;
        let a: Vec3 | null = null;
        if (anchor === now) a = body.active ? body.d : null;
        else if (world.planetAt(body, anchor, this.tmpA)) a = m.mapGlobal(this.tmpA, this.tmpA);
        if (a) {
          const b = this.tmpB;
          b.x = this.ctxPos[k]!;
          b.y = this.ctxPos[k + 1]!;
          b.z = this.ctxPos[k + 2]!;
          m.mapGlobal(b, b);
          sx = w * (a.x - b.x);
          sy = w * (a.y - b.y);
          sz = w * (a.z - b.z);
        }
        // … or, deep in a moon's bubble, the moon's (S4-13)
        const wm = this.moonWeight[i]!;
        if (wm > 0) {
          const moon = world.moons[this.ctxMoon[i]!]!;
          let md: Vec3 | null = null;
          if (anchor === now) md = moon.active ? moon.d : null;
          else if (world.planetAt(body, anchor, this.tmpA) && world.moonOffsetAt(moon, anchor, this.tmpB)) {
            md = m.mapRelative(body.mapped, this.tmpA, this.tmpB, this.tmpC);
          }
          if (md) {
            sx = wm * (md.x - this.moonThen[k]!) + (1 - wm) * sx;
            sy = wm * (md.y - this.moonThen[k + 1]!) + (1 - wm) * sy;
            sz = wm * (md.z - this.moonThen[k + 2]!) + (1 - wm) * sz;
          }
        }
        x += sx;
        y += sy;
        z += sz;
      }
      const settle = phase ? 0 : (now - ti - HOLD_DAYS) / SETTLE_DAYS;
      if (settle > 0) {
        const f = settle >= 1 ? 1 : settle * settle * (3 - 2 * settle);
        const g = this.tmpA;
        g.x = this.pos[k]!;
        g.y = this.pos[k + 1]!;
        g.z = this.pos[k + 2]!;
        m.mapGlobal(g, g);
        x += f * (g.x - x);
        y += f * (g.y - y);
        z += f * (g.z - z);
      }
    }
    if (!force && x === this.disp[k] && y === this.disp[k + 1] && z === this.disp[k + 2]) return;
    this.disp[k] = x;
    this.disp[k + 1] = y;
    this.disp[k + 2] = z;
    this.upload(i);
  }

  /** Writes sample `i`'s display position into the strip (unless its slot holds the head right now). */
  private upload(i: number): void {
    if (i >= this.n || i === this.headSlot) return;
    this.writeSlot(i, this.disp[3 * i]!, this.disp[3 * i + 1]!, this.disp[3 * i + 2]!);
  }

  private writeSlot(slot: number, x: number, y: number, z: number): void {
    const k = 3 * slot;
    // float64 as high + low float32 parts (splitDouble, without its array per call)
    const hx = Math.fround(x);
    const hy = Math.fround(y);
    const hz = Math.fround(z);
    this.high[k] = hx;
    this.high[k + 1] = hy;
    this.high[k + 2] = hz;
    this.low[k] = x - hx;
    this.low[k + 1] = y - hy;
    this.low[k + 2] = z - hz;
    if (slot < this.dirtyMin) this.dirtyMin = slot;
    if (slot > this.dirtyMax) this.dirtyMax = slot;
  }

  /**
   * Re-places the samples that move: those of the last HOLD_DAYS + SETTLE_DAYS (an orbital phase: of the trail
   * memory), and once more those that just left that window (they have settled).
   */
  private moveSamples(revealed: number): void {
    const now = this.world.tdb;
    const ph = this.style.phase;
    const span =
      ph && now >= ph[0] && now - MEMORY_DAYS - MEMORY_MARGIN_DAYS <= ph[1]
        ? Math.max(MEMORY_DAYS + MEMORY_MARGIN_DAYS, HOLD_DAYS + SETTLE_DAYS)
        : HOLD_DAYS + SETTLE_DAYS;
    const lo = lowerBound(this.lineT, now - span);
    const hi = Math.max(revealed, lo);
    // stale samples are not drawn; they are placed when they are mapped again (refreshProfile)
    const v = this.profileVersion;
    for (let i = this.moveLo; i < this.moveHi; i++)
      if ((i < lo || i >= hi) && this.computed[i] && this.mappedWith[i] === v) this.place(i);
    for (let i = lo; i < hi; i++) if (this.computed[i] && this.mappedWith[i] === v) this.place(i);
    for (let i = this.n; i < this.total; i++) if (this.computed[i]) this.place(i);
    this.moveLo = lo;
    this.moveHi = hi;
  }

  /**
   * Re-maps required samples whose global position is from an older profile (a scale-mode change, or samples that
   * became required again), newest first and at most REMAP_PER_FRAME per frame (S4-15 item 3: during the one-second
   * mode morph every sample changes every frame, 3,000–17,000 of them per trail). What is left is stale: from
   * `freshFrom` back, the trail fades out and grows back from the spacecraft once the morph is over. The beacons are
   * re-mapped at once.
   */
  private refreshProfile(changed: boolean): void {
    if (changed) for (let i = this.n; i < this.total; i++) if (this.computed[i]) this.mapFixed(i);
    const r = this.ranges;
    let budget = REMAP_PER_FRAME;
    let stale = -1;
    for (let k = r.length - 2; k >= 0 && stale < 0; k -= 2) {
      for (let i = r[k + 1]! - 1; i >= r[k]!; i--) {
        if (!this.computed[i] || this.mappedWith[i] === this.profileVersion) continue;
        if (budget === 0) {
          stale = i;
          break;
        }
        this.mapFixed(i);
        budget--;
      }
    }
    this.freshFrom = stale < 0 ? -Infinity : this.t[stale]!;
    if (stale < 0) this.staleSince = Number.NaN;
    else if (Number.isNaN(this.staleSince)) this.staleSince = performance.now();
  }

  /** Mission time at or before which required samples may still be mapped with an older profile (−∞: none). */
  private freshFrom = -Infinity;
  /** When the trail got a stale part (performance.now(), ms; NaN: it has none): it fades out from then. */
  private staleSince = Number.NaN;

  /** Moves the head (the spacecraft's position at `now`) into strip slot `slot`, restoring the previous slot. */
  private placeHead(slot: number, now: number): void {
    if (this.headSlot !== slot) {
      const old = this.headSlot;
      this.headSlot = slot;
      if (old >= 0 && old < this.n) {
        this.times[old] = this.t[old]! - this.epoch;
        this.timeDirty(old);
        if (this.computed[old]) this.upload(old);
      }
    }
    if (slot < 0) return;
    const d = this.sc.d;
    this.writeSlot(slot, d.x, d.y, d.z);
    this.times[slot] = now - this.epoch;
    this.timeDirty(slot);
  }

  private timeDirty(slot: number): void {
    if (slot < this.timeMin) this.timeMin = slot;
    if (slot > this.timeMax) this.timeMax = slot;
  }

  /** Flags the changed slots of the strip for upload (only that range). */
  private flush(): void {
    if (this.dirtyMax >= 0) {
      for (const buf of [this.highBuffer, this.lowBuffer]) {
        buf.addUpdateRange(3 * this.dirtyMin, 3 * (this.dirtyMax - this.dirtyMin + 1));
        buf.needsUpdate = true;
      }
      this.dirtyMin = Infinity;
      this.dirtyMax = -1;
    }
    if (this.timeMax >= 0) {
      this.timeBuffer.addUpdateRange(this.timeMin, this.timeMax - this.timeMin + 1);
      this.timeBuffer.needsUpdate = true;
      this.timeMin = Infinity;
      this.timeMax = -1;
    }
  }

  /**
   * @param markerScale beacon size in du at unit distance (0 hides them)
   * @param pulse 0…1, the breathing of the next beacon (0 with reduced motion)
   * @param predictedFrom mission time (days since J2000 TDB) of the real "now": the trail is dashed after it
   */
  update(
    origin: Vec3,
    profileVersion: number,
    frameMode: LineFrame,
    markerScale = 0,
    pulse = 0,
    predictedFrom = Infinity,
  ): void {
    const now = this.world.tdb;
    const sc = this.sc;
    let revealed = 0;
    if (this.n > 0 && now >= this.t[0]!) {
      this.hint = findInterval(this.lineT, now, this.hint);
      revealed = now >= this.t[this.n - 1]! ? this.n : this.hint + 1;
    }
    const profileChanged = profileVersion !== this.profileVersion || frameMode !== this.lastFrameMode;
    this.profileVersion = profileVersion;
    this.lastFrameMode = frameMode;
    this.bodyRelative = frameMode === 'body-relative';
    this.requiredRanges(now, revealed);
    this.lastRevealed = revealed;
    this.requiredReady = this.sample(revealed);
    this.refreshProfile(profileChanged);
    if (profileChanged || now !== this.lastRemapTdb) {
      this.moveSamples(revealed);
      this.lastRemapTdb = now;
    }
    // the head: a segment from the newest revealed sample to the spacecraft (strip slot `revealed`)
    const head = sc.visible && revealed >= 1 && revealed < this.n + 1;
    this.placeHead(head ? revealed : -1, now);
    this.geometry.instanceCount = head ? revealed : Math.max(0, revealed - 1);
    this.flush();

    this.root.visible = this.requiredReady;
    if (!this.requiredReady) return;
    const u = this.uniforms;
    const [hx, lx] = splitDouble(origin.x);
    const [hy, ly] = splitDouble(origin.y);
    const [hz, lz] = splitDouble(origin.z);
    u.uOriginHigh.value.set(hx, hy, hz);
    u.uOriginLow.value.set(lx, ly, lz);
    u.uNow.value = now - this.epoch;
    u.uFreshFrom.value = Number.isFinite(this.freshFrom) ? this.freshFrom - this.epoch : -NEVER;
    u.uStaleAlpha.value = Number.isNaN(this.staleSince)
      ? 0
      : Math.max(0, 1 - (performance.now() - this.staleSince) / STALE_FADE_MS);
    u.uPredictedFrom.value = Number.isFinite(predictedFrom) ? predictedFrom - this.epoch : NEVER;

    // flyby beacons (S1-22, S2-10): reached ones solid, upcoming ones dimmer, the next one pulses
    let next = -1;
    this.markerSprites.forEach(({ t }, j) => {
      if (t > now && (next < 0 || t < this.markerSprites[next]!.t)) next = j;
    });
    const dimmed = this.highlight === 'dimmed';
    const gain = this.hdr && !dimmed ? BEACON_HDR : 1;
    this.markerSprites.forEach(({ t, sprite }, j) => {
      const i = this.n + j;
      sprite.visible = sc.visible && markerScale > 0 && this.computed[i] === 1;
      if (!sprite.visible) return;
      const k = 3 * i;
      sprite.position.set(this.disp[k]! - origin.x, this.disp[k + 1]! - origin.y, this.disp[k + 2]! - origin.z);
      const scale = markerScale * (j === next ? 1 + 0.35 * pulse : 1);
      sprite.scale.set(scale, scale, 1);
      const level = (now >= t ? gain : BEACON_FUTURE) * (dimmed ? DIMMED : 1);
      sprite.material.color.copy(this.beaconColor).multiplyScalar(level);
    });
  }

  // ── tests ────────────────────────────────────────────────────────────────

  /** Display position of line sample `i` (du), or null if not computed. */
  samplePosition(i: number): Vec3 | null {
    if (!this.computed[i]) return null;
    return vec3(this.disp[3 * i]!, this.disp[3 * i + 1]!, this.disp[3 * i + 2]!);
  }

  /** True heliocentric position of line sample `i` (km), or null if not computed. */
  sampleTruePosition(i: number): Vec3 | null {
    if (!this.computed[i]) return null;
    return vec3(this.pos[3 * i]!, this.pos[3 * i + 1]!, this.pos[3 * i + 2]!);
  }

  sampleTime(i: number): number {
    return this.t[i]!;
  }

  /** Strip slot `slot` as float64 (high + low). */
  stripSlot(slot: number): Vec3 {
    const k = 3 * slot;
    return vec3(
      this.high[k]! + this.low[k]!,
      this.high[k + 1]! + this.low[k + 1]!,
      this.high[k + 2]! + this.low[k + 2]!,
    );
  }

  /** Mission time (days since J2000 TDB) the shader sees at strip slot `slot`. */
  stripTime(slot: number): number {
    return this.times[slot]! + this.epoch;
  }
}

/** First index with a[i] ≥ x (a sorted). */
function lowerBound(a: Float64Array, x: number): number {
  let lo = 0;
  let hi = a.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (a[mid]! < x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
